import React, { useState } from 'react';
import { ImageList, ImageListItem, ImageListItemBar, IconButton, Menu, MenuItem, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, Alert, CircularProgress, TextField, Box, keyframes, Chip } from '@mui/material';
import { MoreVert, Delete, Public, ContentCopy, Close, AutoAwesome, Label } from '@mui/icons-material';
import { Image, Tag } from '../../types';
import { deleteImage, makeImagePublic, describeImageById, updateImageAlt } from '../../services/images';
import { useLang } from '../../contexts/langContext';
import ProtectedImage from '../common/ProtectedImage';
import ManageTagsDialog from '../common/ManageTagsDialog';
import { useTags } from '../../hooks/useTags';

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
    transform: scale(1);
  }
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

interface ImageListProps {
  images: Image[];
  onImageUpdate?: () => void;
  isUserImages: boolean;
  onImageSelect?: (imageId: string) => void;
  highlightImageId?: string | null;
  tags?: Tag[];
}

function ImageListComponent({ images, onImageUpdate, isUserImages, onImageSelect, highlightImageId, tags: externalTags }: ImageListProps) {
  const { lang } = useLang();
  const { tags: fetchedTags } = useTags();
  const tags = externalTags || fetchedTags;
  const tagMap = React.useMemo(() => {
    const map: Record<string, Tag> = {};
    tags.forEach(t => {
      map[t._id] = t;
    });
    return map;
  }, [tags]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<Image | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, image: Image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    const imageToDelete = selectedImage;
    handleMenuClose();
    if (imageToDelete) {
      await deleteImage(imageToDelete._id);
      if (onImageUpdate) onImageUpdate();
      setSelectedImage(null);
    }
  };

  const handleMakePublic = async (): Promise<{ success: boolean; message?: string }> => {
    if (!selectedImage) return { success: false, message: 'No image selected. Please try again.' };
    try {
      await makeImagePublic(selectedImage._id);
      if (onImageUpdate) onImageUpdate();
      handleMenuClose();
      return { success: true };
    } catch (error: any) {
      console.error("Failed to make image public:", error);
      handleMenuClose();
      const errorMessage = error?.response?.data?.message || error?.message || 'An unknown error occurred';
      return { success: false, message: errorMessage };
    }
  };

  const handleConfirmOpen = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmOpen(false);
    setSelectedImage(null);
  };

  const handleConfirmMakePublic = async () => {
    handleConfirmClose();
    const result = await handleMakePublic();
    if (result.success) {
      setSnackbarMessage(lang === 'en' ? 'Image successfully made public.' : '图片已成功设为公开。');
      setSnackbarSeverity('success');
    } else {
      const baseMessage = lang === 'en' ? 'Failed to make image public' : '将图片设为公开失败';
      const reason = result.message ? `: ${result.message}` : '';
      setSnackbarMessage(`${baseMessage}${reason}`);
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
    setSelectedImage(null);
  };

  const handleImageClick = (image: Image) => {
    if (onImageSelect) {
      onImageSelect(image._id);
    } else {
      setPreviewImage(image);
      setPreviewOpen(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewImage(null);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleGenerateDescription = async () => {
    if (!selectedImage) return;
    
    // Open the dialog immediately
    setIsDescriptionDialogOpen(true);
    
    // Initialize with existing description if any
    setEditedDescription(selectedImage.alt || '');
    
    // Start generating in the background
    setIsGeneratingDescription(true);
    
    handleMenuClose();
    
    try {
      // Get the AI-generated description
      const description = await describeImageById(selectedImage._id, undefined, lang);
      
      // Update the dialog with the generated description
      setEditedDescription(description);
      
    } catch (error) {
      console.error('Error generating description:', error);
      setSnackbarMessage(lang === 'en' 
        ? 'Failed to generate description' 
        : '生成描述失败');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleDescriptionDialogClose = () => {
    setIsDescriptionDialogOpen(false);
    setEditedDescription('');
  };

  const handleSaveDescription = async () => {
    if (!selectedImage) return;
    
    try {
      // Save the edited description to the database
      await updateImageAlt(selectedImage._id, editedDescription);
      
      setSnackbarMessage(lang === 'en' 
        ? 'Description updated successfully' 
        : '描述更新成功');
      setSnackbarSeverity('success');
      
      // Close the edit dialog
      handleDescriptionDialogClose();
      
      // If there's an onImageUpdate callback, call it to refresh the list
      if (onImageUpdate) onImageUpdate();
      
    } catch (error) {
      console.error('Error updating description:', error);
      setSnackbarMessage(lang === 'en' 
        ? 'Failed to update description' 
        : '更新描述失败');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleManageTags = () => {
    setManageTagsDialogOpen(true);
    handleMenuClose();
  };

  const handleTagsSaved = (newTagIds: string[]) => {
    if (onImageUpdate) {
      onImageUpdate();
    }
    setSnackbarMessage(lang === 'en' ? 'Tags updated successfully' : '标签更新成功');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  return (
    <>
      <ImageList variant="masonry" cols={4} gap={8}>
        {images.map((image) => {
          const isHighlighted = highlightImageId === image._id;
          
          return (
            <ImageListItem 
              key={image._id} 
              onClick={() => handleImageClick(image)}
              sx={{
                position: 'relative',
                overflow: 'visible',
                ...(isHighlighted && {
                  animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, transparent 30%, rgba(33, 150, 243, 0.3) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmerAnimation} 3s linear infinite`,
                    pointerEvents: 'none',
                    zIndex: 1,
                  },
                  '& img': {
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                  }
                })
              }}
            >
              <ProtectedImage
                imageUrl={image.url}
                alt={image.alt}
                loading="lazy"
                style={{ cursor: onImageSelect ? 'pointer' : 'default' }}
              />
              
              {/* Tags positioned at top-left corner */}
              {(image.tagIds || []).length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxWidth: 'calc(100% - 16px)',
                    zIndex: 2,
                  }}
                >
                  {(image.tagIds || [])
                    .slice(0, 3)
                    .map((tagId: string) => {
                      const tagName = tagMap[tagId]?.name || tagId;
                      return (
                        <Chip
                          key={tagId}
                          label={tagName}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            fontSize: '0.65rem',
                            height: 20,
                            maxWidth: 100,
                            '& .MuiChip-label': {
                              paddingLeft: '8px',
                              paddingRight: '8px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            }
                          }}
                        />
                      );
                    })}
                  {(image.tagIds || []).length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.65rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 20,
                      }}
                    >
                      ...
                    </Typography>
                  )}
                </Box>
              )}
              
              <ImageListItemBar
                title={
                  <Typography variant="caption">{image.alt}</Typography>
                }
                actionIcon={
                  isUserImages && (
                    <IconButton
                      sx={{ color: 'white', fontSize: '0.8rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, image);
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )
                }
              />
              {isHighlighted && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    zIndex: 2,
                    boxShadow: 2,
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 16 }} />
                  {lang === 'en' ? 'NEW' : '新'}
                </Box>
              )}
            </ImageListItem>
          );
        })}
      </ImageList>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        <MenuItem onClick={handleManageTags}>
          <Label sx={{ mr: 1 }} /> {lang === 'en' ? 'Manage Tags' : '标签'}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete sx={{ mr: 1 }} /> {lang === 'en' ? 'Delete' : '删除'}
        </MenuItem>
        {selectedImage && !selectedImage.isPublic && (
          <MenuItem onClick={handleConfirmOpen}>
            <Public sx={{ mr: 1 }} /> {lang === 'en' ? 'Make Public' : '设为公开'}
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          navigator.clipboard.writeText(selectedImage?.alt || '');
          handleMenuClose();
          setSelectedImage(null);
        }}>
          <ContentCopy sx={{ mr: 1 }} /> {lang === 'en' ? 'Copy description' : '复制描述'}
        </MenuItem>
        {selectedImage && isUserImages && (
          <MenuItem onClick={handleGenerateDescription} disabled={isGeneratingDescription}>
            <AutoAwesome sx={{ mr: 1 }} /> 
            {lang === 'en' ? 'Generate AI description' : '生成AI描述'}
            {isGeneratingDescription && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </MenuItem>
        )}
      </Menu>
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="lg"
        onClick={handlePreviewClose}
      >
        <IconButton
          onClick={handlePreviewClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.4)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.6)',
            },
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>
        {previewImage && (
          <Box sx={{ position: 'relative' }}>
            <ProtectedImage
              imageUrl={previewImage.url}
              alt={previewImage.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
            />
            {previewImage.alt && previewImage.alt.trim() !== '' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: 2,
                  maxHeight: '30%',
                  overflow: 'auto',
                }}
              >
                <Typography variant="body1">
                  {previewImage.alt}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Dialog>
      <Dialog
        open={isConfirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {lang === 'en' ? 'Confirm Making Image Public' : '确认将图片设为公开'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {lang === 'en'
              ? 'Making this image public is an irreversible action. The image will be copied, and it may be used by other users. You will not be able to make it private again. Do you wish to proceed?'
              : '将此图片设为公开是不可逆的操作。图片将被复制，并可能被其他用户使用。您将无法再将其设为私有。您确定要继续吗？'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button onClick={handleConfirmMakePublic} autoFocus>
            {lang === 'en' ? 'Confirm' : '确认'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDescriptionDialogOpen}
        onClose={handleDescriptionDialogClose}
        aria-labelledby="description-dialog-title"
        aria-describedby="description-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="description-dialog-title">
          {lang === 'en' ? 'AI Image Description' : 'AI图像描述'}
        </DialogTitle>
        <DialogContent>
          {isGeneratingDescription ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography>
                {lang === 'en' 
                  ? 'Generating image description...' 
                  : '正在生成图像描述...'}
              </Typography>
            </Box>
          ) : (
            <>
              <DialogContentText id="description-dialog-description" sx={{ mb: 2 }}>
                {lang === 'en'
                  ? 'The AI has generated a description for this image. You can edit it before saving.'
                  : 'AI已生成此图像的描述。您可以在保存前编辑它。'}
              </DialogContentText>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label={lang === 'en' ? 'Description' : '描述'}
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDescriptionDialogClose}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button 
            onClick={handleSaveDescription} 
            color="primary"
            disabled={isGeneratingDescription}
          >
            {lang === 'en' ? 'Save Description' : '保存描述'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ManageTagsDialog
        open={manageTagsDialogOpen}
        onClose={() => setManageTagsDialogOpen(false)}
        entityType="image"
        entity={selectedImage}
        onSaved={handleTagsSaved}
      />
    </>
  );
}

export default ImageListComponent;
